import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import { PlusIcon } from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { z } from "zod";

import { DuplicateUserDialog } from "./DuplicateUserDialog";
import {
  CandidacyStatusSelectContent,
  Column,
  ConfirmationDialog,
  ErrorText,
  LoadingIndicator,
  LocationInput,
  Row,
} from "@/components";
import { CandidateResumeRecoveryDialog } from "@/components/companies/jobs/pipeline";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { FormTextInput } from "@/components/form/FormTextInput";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useReportUnexpectedNull } from "@/hooks/useReportUnexpectedNull";
import { useSelectedResourceIdFromUrl } from "@/hooks/useSelectedResourceIdFromUrl";
import { useCreateCandidacy } from "@/hooks/mutations/useCreateCandidacy";
import { useAddCandidacyInCandidaciesMinimal } from "@/hooks/sets/useAddCandidacyInCandidaciesMinimal";
import {
  desiredPositionOptions,
  sourceOptions,
  stafferSelectableCandidacySourceOptions,
} from "@/lib/constants";
import { files } from "@/lib/files";
import { guacHttpClient } from "@/lib/http";
import { locations } from "@/lib/locations";
import { misc } from "@/lib/misc";
import { resumePreparation } from "@/lib/resumePreparation";
import { optionalResumeFileSchema } from "@/lib/schemas";
import { sort } from "@/lib/sort";
import { novaClient } from "@/lib/trpc";
import {
  CandidacySource,
  CandidacyStatus,
  CandidacyWithProfileResponse,
  CreateUserRequest,
  ExistingOrPendingUser,
  ExistingOrPendingUserWithDetails,
  GetOtherCandidaciesResponse,
  HttpResponse,
  MinimalCandidacy,
  MinimalCandidacyStatusChange,
  MinimalScore,
  PreservedFormData,
  ResumeParserAction,
  ResumeUpload,
} from "@/types";

const sortedSourceOptions = sourceOptions.sort((a, b) =>
  a.label.localeCompare(b.label),
);

// Detects if query is a phone number by checking if the string contains any digits.
const isPhoneNumber = (query: string): boolean => {
  return /\d/.test(query);
};

const pendingUserSchema = z.object({
  name: z
    .string()
    .min(1, "Required")
    .refine(
      (arg) => {
        const words = arg.trim().split(" ");
        return words.length >= 2;
      },
      {
        message: "First and last name required",
      },
    ),
  phone: z
    .string()
    .min(1, "Required")
    .refine(
      (phone) => {
        // Pattern: 1XXXXXXXXXX (1 followed by 10 digits).
        const phoneRegex = /^1\d{10}$/;
        return phoneRegex.test(phone);
      },
      {
        message: "Must be 11 digits (no spaces) starting with 1",
      },
    ),
  // TODO: Add email validation.
  email: z.string(),
  desiredPosition: z.string().min(1, "Required"),
  location: z
    .string()
    .min(1, "Required")
    .refine(
      (location) =>
        locations.validateLocation({ enforceFullLocation: true, location }),
      {
        message:
          "Must be City, State, USA with a valid state code (e.g Knoxville, TN, USA)",
      },
    ),
  screeningQuestionsAndAnswers: z.string(),
  resumeFile: optionalResumeFileSchema,
  source: z.string().min(1, "Required"),
  status: z.nativeEnum(CandidacyStatus),
  candidacySource: z.nativeEnum(CandidacySource, {
    message: "Required",
  }),
});

const UserAndCandidacyCreationLoadingIndicator = () => {
  return (
    <LoadingIndicator
      className="fixed inset-0 z-50 bg-black/80"
      variant="large"
    />
  );
};

interface PendingUserFormProps {
  defaultStatus: CandidacyStatus;
  isSubmitting: boolean;
  initialValues: {
    name?: string;
    phone?: string;
  };
  onSubmit: ({
    createUserRequest,
    resumeUpload,
    screeningQuestionsAndAnswers,
    source,
    status,
  }: {
    createUserRequest: CreateUserRequest;
    resumeUpload?: ResumeUpload;
    screeningQuestionsAndAnswers: string;
    source: CandidacySource;
    status: CandidacyStatus;
  }) => void;
  onCancel: () => void;
  onFilledChange: (isFilled: boolean) => void;
  submissionError: string | null;
}

const PendingUserForm = ({
  defaultStatus,
  isSubmitting,
  initialValues,
  onCancel,
  onFilledChange,
  onSubmit,
  submissionError,
}: PendingUserFormProps) => {
  const form = useForm<z.infer<typeof pendingUserSchema>>({
    defaultValues: {
      name: initialValues.name ?? "",
      phone: initialValues.phone ?? "",
      email: "",
      desiredPosition: "",
      location: "",
      screeningQuestionsAndAnswers: "",
      source: "",
      status: defaultStatus,
      candidacySource: undefined,
    },
    resolver: zodResolver(pendingUserSchema),
  });
  const formValues = useWatch({ control: form.control });
  const isFilled = [
    formValues.name,
    formValues.phone,
    formValues.email,
    formValues.desiredPosition,
    formValues.location,
    formValues.screeningQuestionsAndAnswers,
    formValues.resumeFile,
    formValues.source,
    formValues.candidacySource,
  ].some(Boolean);

  useEffect(() => {
    onFilledChange(isFilled);
  }, [isFilled, onFilledChange]);

  const handleSubmit = async (data: z.infer<typeof pendingUserSchema>) => {
    const [firstName, ...lastNameParts] = data.name.trim().split(" ");
    const lastName = lastNameParts.join(" ");

    let resumeUpload: ResumeUpload | undefined;
    if (data.resumeFile) {
      let resumeFileBase64: string | ArrayBuffer | null;
      try {
        resumeFileBase64 = await misc.convertFileToBase64(data.resumeFile);
      } catch {
        form.setError("resumeFile", {
          message:
            "Resume file could not be read. Please check that the file still exists on your computer and try again.",
        });
        return;
      }
      resumeUpload = {
        fileBase64: resumeFileBase64 as string,
        fileName: data.resumeFile.name,
        title: files.formatDefaultResumeTitle(data.resumeFile),
      };
    }

    onSubmit({
      createUserRequest: {
        firstName,
        lastName,
        phone: data.phone,
        desiredPosition: data.desiredPosition,
        location: data.location,
        source: data.source,
        ...(data.email && { email: data.email }),
      },
      resumeUpload,
      screeningQuestionsAndAnswers: data.screeningQuestionsAndAnswers,
      source: data.candidacySource,
      status: data.status,
    });
  };

  return (
    <Column className="gap-4 p-6">
      <p className="text-2xl font-semibold text-slate-700">
        Manually create a pending user
      </p>
      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FormTextInput
            fieldName="name"
            form={form}
            label="Name"
            placeholder="First and last name"
          />
          <FormTextInput
            fieldName="phone"
            form={form}
            label="Phone"
            placeholder="Phone"
          />
          <FormTextInput
            fieldName="email"
            form={form}
            label="Email"
            optional
            placeholder="Email"
          />
          <FormEnumSelect
            fieldName="desiredPosition"
            form={form}
            label="Desired position"
            options={desiredPositionOptions}
            placeholder="Desired position"
          />
          <LocationInput fieldName="location" formControl={form.control} />
          <FormField
            control={form.control}
            name="screeningQuestionsAndAnswers"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Screening Questions (optional)</FormLabel>
                <FormControl>
                  <Textarea placeholder="Screening questions" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="resumeFile"
            render={({ field: { onChange, ...field } }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Resume (optional)</FormLabel>
                <FormControl>
                  <Input
                    accept=".doc,.docx,.pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      onChange(file ?? undefined);
                    }}
                    type="file"
                    {...field}
                    // QUESTION: Why is this needed?
                    value={undefined}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormEnumSelect
            fieldName="source"
            form={form}
            label="Source"
            options={sortedSourceOptions}
            placeholder="Source"
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <CandidacyStatusSelectContent />
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormEnumSelect
            fieldName="candidacySource"
            form={form}
            label="Candidacy Source"
            options={stafferSelectableCandidacySourceOptions}
            placeholder="Select source"
          />
          <Column className="gap-2">
            <Row className="items-center justify-end gap-2">
              <Button
                className="rounded-full"
                disabled={isSubmitting}
                onClick={onCancel}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                className="relative rounded-full"
                disabled={isSubmitting}
                type="submit"
              >
                <span className={isSubmitting ? "invisible" : ""}>
                  Create pending user
                </span>
                {isSubmitting && (
                  <LoadingIndicator
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    gearColor="white"
                    variant="small"
                  />
                )}
              </Button>
            </Row>
            {submissionError && (
              <Row className="justify-end">
                <ErrorText errorMessage={submissionError} />
              </Row>
            )}
          </Column>
        </form>
      </Form>
    </Column>
  );
};

const candidacyDetailsSchema = z.object({
  source: z.nativeEnum(CandidacySource),
  status: z.nativeEnum(CandidacyStatus),
});

const CandidacyDetailsDialog = ({
  defaultStatus,
  onConfirm,
  onOpenChange,
  open,
  user,
}: {
  defaultStatus: CandidacyStatus;
  onConfirm: (source: CandidacySource, status: CandidacyStatus) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  user: ExistingOrPendingUser;
}) => {
  const form = useForm<z.infer<typeof candidacyDetailsSchema>>({
    defaultValues: {
      status: defaultStatus,
    },
    resolver: zodResolver(candidacyDetailsSchema),
  });

  const handleSubmit = (data: z.infer<typeof candidacyDetailsSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Candidacy details submit",
      MIXPANEL_SCREENS.JOB,
      {
        status: data.status,
        source: data.source,
      },
    );
    onConfirm(data.source, data.status);
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        // Reset the form when the dialog is closed.
        form.reset();
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Select Candidacy Details for {user.firstName} {user.lastName}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <CandidacyStatusSelectContent />
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Source</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                    </FormControl>
                    <CandidacySourceSelectContent />
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Row className="justify-end">
              <Button className="rounded-full" type="submit">
                Create Candidacy
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const CandidacySourceSelectContent = () => {
  return (
    <SelectContent>
      {stafferSelectableCandidacySourceOptions.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  );
};

/**
 * Converts a CandidacyWithProfileResponse to MinimalCandidacy format.
 * Used for updating the minimal candidacies cache.
 */
const convertToMinimalCandidacy = (
  candidacyWithProfileResponse: CandidacyWithProfileResponse,
): MinimalCandidacy => {
  const { candidacy, candidateProfile, lastCandidacyStatusChange } =
    candidacyWithProfileResponse;

  const agenticScores: MinimalScore[] =
    candidacyWithProfileResponse.candidacyAgenticScoreChanges.map(
      (scoreChange) => ({
        toScore: scoreChange.toScore,
        fromScore: scoreChange.fromScore,
        createdAt: scoreChange.createdAt,
        justification: scoreChange.justification,
        userId: scoreChange.userId,
      }),
    );

  // Convert lastCandidacyStatusChange to minimal format
  const minimalStatusChange: MinimalCandidacyStatusChange = {
    createdAt: new Date().toISOString(),
    fromStatus: lastCandidacyStatusChange.fromStatus,
    toStatus: lastCandidacyStatusChange.toStatus,
    statusChangeReason: lastCandidacyStatusChange.statusChangeReason,
    byAgent: false,
  };

  const mostRecentWorkExperience = sort
    .sortWorkExperiences(candidateProfile.workExperience)
    .at(0);

  return {
    id: candidacy.id,
    createdAt: candidacy.createdAt,
    talentId: candidacy.talentId,
    firstName: candidacy.firstName,
    lastName: candidacy.lastName,
    email: candidateProfile.email,
    mostRecentJobTitle: mostRecentWorkExperience?.title ?? null,
    mostRecentCompanyName:
      mostRecentWorkExperience?.internalCompany?.name ??
      mostRecentWorkExperience?.company ??
      null,
    location: candidateProfile.profile.location,
    lastCandidacyStatusChange: minimalStatusChange,
    agenticScores,
    owners: candidacyWithProfileResponse.candidacyOwners.map((o) => ({
      id: o.id,
      userId: o.userId,
    })),
    voiceAiOutreach: candidacyWithProfileResponse.voiceAIOutreachSlim,
    source: candidacy.source,
    isExternallySourced: candidacy.isExternallySourced,
    isAutoResurrected: candidacy.isAutoResurrected,
    contactsAttempted: null,
    nextActionAt: null,
    commuteInfo: candidacyWithProfileResponse.commuteInfo,
    lastPing: candidacyWithProfileResponse.lastPing,
    quoConversationId: null,
    phone: candidacy.phone,
    stafferAgentActive: candidacy.stafferAgentActive,
    nextScheduledAgentMessageAt: null,
    stevieCallDuration: null,
    elevatedFrom: null,
    stevieNudgedAt: null,
    stevieCallEndedAt: null,
  };
};

// How the user is leaving the manual-add form; CLOSE also dismisses the dialog.
enum ManualExitAction {
  CANCEL = "CANCEL",
  CLOSE = "CLOSE",
}

export const AddCandidateDialog = ({
  defaultStatus = CandidacyStatus.SHORTLIST,
  open,
  onOpenChange,
}: {
  defaultStatus?: CandidacyStatus;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const addCandidacyInCandidaciesMinimal =
    useAddCandidacyInCandidaciesMinimal();
  const { mutate: createCandidacy, isPending: isCreatingCandidacy } =
    useCreateCandidacy();
  const {
    mutate: createUser,
    isPending: isCreatingUser,
    error: createUserError,
    reset: resetCreateUser,
  } = novaClient.internal.user.active.create.useMutation();
  const {
    mutate: createResume,
    mutateAsync: createResumeAsync,
    isPending: isCreatingResume,
  } = novaClient.internal.user.resume.active.create.useMutation();
  const { mutateAsync: parseResume, isPending: isParsingResume } =
    novaClient.internal.user.resume.active.parse.useMutation();
  const [isPreparingResume, setIsPreparingResume] = useState(false);
  const [resumeRecovery, setResumeRecovery] = useState<{
    canRetry: boolean;
    retry: () => void;
  } | null>(null);
  const isCreating =
    isCreatingCandidacy ||
    isCreatingUser ||
    isCreatingResume ||
    isParsingResume ||
    isPreparingResume;
  const currentJobId = useCurrentJobId();
  // TODO: We have bigger fish to fry right now but this should really be passed in from the parent.
  // The best solution is to combine and pass down logic at the `<JobPipelineTableContainer />` level.
  const { setSelectedResourceId: setSelectedCandidacyId } =
    useSelectedResourceIdFromUrl({
      paramName: "c",
    });
  const [query, setQuery] = useState("");
  // Doubles as the details dialog's open state — it renders only for a selected
  // user, so a separate flag could disagree with it and wedge the UI.
  const [selectedUser, setSelectedUser] =
    useState<ExistingOrPendingUser | null>(null);
  const [showPendingUserForm, setShowPendingUserForm] = useState(false);
  const [isPendingUserFormFilled, setIsPendingUserFormFilled] = useState(false);
  const [pendingManualExit, setPendingManualExit] =
    useState<ManualExitAction | null>(null);
  const [debouncedQuery] = useDebounce(query, 250); // Can adjust this accordingly.
  const [showDuplicateUserDialog, setShowDuplicateUserDialog] = useState(false);
  const [duplicateUser, setDuplicateUser] =
    useState<ExistingOrPendingUserWithDetails | null>(null);
  const [preservedFormData, setPreservedFormData] =
    useState<PreservedFormData | null>(null);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorToast = useErrorToast();
  const reportUnexpectedNull = useReportUnexpectedNull();

  // Nova packs the duplicate-user payload into the error message as JSON.
  const parsedCreateUserError = (() => {
    if (!createUserError?.message) return null;
    try {
      return JSON.parse(createUserError.message) as {
        message: string;
        existingUser: ExistingOrPendingUserWithDetails | null;
      };
    } catch {
      return { message: createUserError.message, existingUser: null };
    }
  })();
  // Duplicate-user errors are handled by the DuplicateUserDialog — showing them
  // inline too causes a red flash under the form before the dialog opens.
  const inlineSubmissionError = parsedCreateUserError?.existingUser
    ? null
    : (parsedCreateUserError?.message ?? null);

  const { data: searchResults } =
    novaClient.internal.user.search.byNameOrPhone.useQuery(
      {
        query: debouncedQuery,
      },
      {
        enabled: !!debouncedQuery,
      },
    );

  const formatUserName = (user: ExistingOrPendingUser) => {
    const firstName = user.firstName ?? "";
    const lastName = user.lastName ?? "";
    const phone = user.phone;
    const name = `${firstName} ${lastName}`.trim();
    return name ? `${name} (${phone ?? ""})` : (phone ?? "");
  };

  const handleOpenChange = (open: boolean) => {
    // Reopening cancels a close that is still animating out, so its pending
    // cleanup can't land on the state of the session that just started.
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    if (!open) {
      // Reset the query and pending user form when the dialog is closed. We wait
      // to avoid things from being cleared while the dialog is being closed.
      resetTimeoutRef.current = setTimeout(() => {
        resetTimeoutRef.current = null;
        // The duplicate-user flow closes this dialog but still needs its state.
        if (showDuplicateUserDialog) return;
        setQuery("");
        setShowPendingUserForm(false);
        setSelectedUser(null);
        setResumeRecovery(null);
        resetCreateUser();
      }, 150);
    }
    onOpenChange(open);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");

    const formattedText = misc.formatPhoneNumberOnPaste(pastedText);
    setQuery(formattedText);
  };

  const completeManualExit = (exit: ManualExitAction) => {
    setPendingManualExit(null);
    setIsPendingUserFormFilled(false);
    setShowPendingUserForm(false);
    if (exit === ManualExitAction.CLOSE) handleOpenChange(false);
  };

  // Exiting a filled form pauses for discard confirmation first.
  const handleManualExit = (exit: ManualExitAction) => {
    if (isCreating) return;
    if (isPendingUserFormFilled) {
      setPendingManualExit(exit);
      return;
    }
    completeManualExit(exit);
  };

  const handleSelectExistingOrPendingUser = ({
    user,
  }: {
    user: ExistingOrPendingUser;
  }) => {
    setSelectedUser(user);
  };

  const handleCreateNewExistingOrPendingCandidacy = ({
    resumeUpload,
    screeningQuestionsAndAnswers,
    source,
    status,
    user,
  }: {
    resumeUpload?: ResumeUpload;
    screeningQuestionsAndAnswers?: string;
    source: CandidacySource;
    status?: CandidacyStatus;
    user: ExistingOrPendingUser;
  }) => {
    createCandidacy(
      {
        createCandidacyRequest: {
          talentId: user.id,
          ...(screeningQuestionsAndAnswers?.trim() && {
            screeningAnswers: screeningQuestionsAndAnswers.trim(),
          }),
          source,
          status: status ?? defaultStatus,
        },
        currentJobId,
      },
      {
        onSuccess: (createdCandidacyWithProfileResponse) => {
          // Create the resume only after the candidacy succeeds to avoid
          // orphaned uploads. No parse — don't clobber an existing profile.
          if (resumeUpload) {
            const resumePayload = {
              ...resumeUpload,
              userId: user.id,
            };
            createResume(resumePayload);
          }

          // Close the dialog first.
          handleOpenChange(false);

          // Add the new candidacy to the cache.
          addCandidacyInCandidaciesMinimal({
            minimalCandidacy: convertToMinimalCandidacy(
              createdCandidacyWithProfileResponse,
            ),
          });

          // Then set the URL parameter to open the sheet after a small delay.
          setTimeout(() => {
            setSelectedCandidacyId(
              createdCandidacyWithProfileResponse.candidacy.id,
            );
          }, 150);
        },
        onError: () => {
          // Reopen the search dialog on failure to allow user to retry. The
          // details dialog stays closed — the failure is surfaced by a toast.
          handleOpenChange(true);
        },
      },
    );
  };

  const handleAddUserToPipeline = () => {
    if (!duplicateUser || !preservedFormData) return;
    const user = duplicateUser;
    const formData = preservedFormData;

    setShowDuplicateUserDialog(false);
    setDuplicateUser(null);
    setPreservedFormData(null);
    resetCreateUser();

    handleCreateNewExistingOrPendingCandidacy({
      resumeUpload: formData.resumeUpload,
      screeningQuestionsAndAnswers: formData.screeningQuestionsAndAnswers,
      source: formData.source,
      status: formData.status,
      user,
    });
  };

  const handleSelectExistingOrPendingUserConfirm = (
    source: CandidacySource,
    status: CandidacyStatus,
  ) => {
    if (!selectedUser) {
      reportUnexpectedNull(
        "AddCandidateDialog.handleSelectExistingOrPendingUserConfirm",
        "selectedUser",
      );
      return;
    }

    // Close input dialog and user candidacy details dialog to allow for clean loading indicator display.
    handleOpenChange(false);
    setSelectedUser(null);

    handleCreateNewExistingOrPendingCandidacy({
      source,
      status,
      user: selectedUser,
    });
  };

  const handleCreateUserAndCandidacy = ({
    createUserRequest,
    resumeUpload,
    screeningQuestionsAndAnswers,
    source,
    status,
  }: {
    createUserRequest: CreateUserRequest;
    resumeUpload?: ResumeUpload;
    screeningQuestionsAndAnswers: string;
    source: CandidacySource;
    status: CandidacyStatus;
  }) => {
    if (isCreating || resumeRecovery) return;
    mixpanelUtils.trackButtonClick("Create user", MIXPANEL_SCREENS.JOB, {
      has_email: !!createUserRequest.email,
      has_resume: !!resumeUpload,
      has_screening_answers: !!screeningQuestionsAndAnswers,
      source,
      status,
    });
    createUser(createUserRequest, {
      onError: (error) => {
        try {
          const parsed = JSON.parse(error.message) as {
            message: string;
            existingUser: ExistingOrPendingUserWithDetails | null | undefined;
          };
          if (!parsed.existingUser) return;
          const existingUser = parsed.existingUser;

          // If they already have a candidacy for this job there's nothing to
          // offer — otherwise hand off to the duplicate-user dialog.
          const queryParams = new URLSearchParams();
          queryParams.append("talentId", existingUser.id.toString());
          void guacHttpClient
            .get<
              GetOtherCandidaciesResponse[],
              HttpResponse<GetOtherCandidaciesResponse[]>
            >(`other-candidacies?${queryParams.toString()}`)
            .then((res) => {
              const alreadyInPipeline = res.data.some(
                (candidacy) => candidacy.job.id === currentJobId,
              );
              if (alreadyInPipeline) {
                errorToast({
                  description:
                    "This user already has a candidacy for this job.",
                });
                resetCreateUser();
                return;
              }

              setDuplicateUser(existingUser);
              setPreservedFormData({
                createUserRequest,
                resumeUpload,
                screeningQuestionsAndAnswers,
                source,
                status,
              });
              setShowDuplicateUserDialog(true);
              onOpenChange(false);
              setShowPendingUserForm(false);
              resetCreateUser();
            });
        } catch (parseError) {
          Sentry.captureException(parseError);
        }
      },
      onSuccess: (user) => {
        const createCandidacyForUser = () => {
          handleCreateNewExistingOrPendingCandidacy({
            screeningQuestionsAndAnswers,
            status,
            source,
            user: {
              id: user.id,
              firstName: createUserRequest.firstName,
              lastName: createUserRequest.lastName,
              phone: createUserRequest.phone,
            },
          });
        };

        if (!resumeUpload) {
          createCandidacyForUser();
          return;
        }

        const attempt = resumePreparation.createAttempt({
          save: () =>
            createResumeAsync({
              ...resumeUpload,
              userId: user.id,
            }),
          parse: (id) =>
            parseResume({ action: ResumeParserAction.OVERWRITE, id }),
          onReady: () => {
            setResumeRecovery(null);
            createCandidacyForUser();
          },
        });
        const prepareResume = async () => {
          setIsPreparingResume(true);
          try {
            await attempt.run();
          } catch {
            setResumeRecovery({
              canRetry: attempt.hasSavedResume(),
              retry: () => {
                void prepareResume();
              },
            });
            handleOpenChange(true);
            errorToast({
              description: "Candidate saved, but the resume step failed.",
            });
          } finally {
            setIsPreparingResume(false);
          }
        };
        void prepareResume();
      },
    });
  };

  const renderSearchOrManualDialog = () => {
    if (resumeRecovery) {
      return (
        <CandidateResumeRecoveryDialog
          canRetry={resumeRecovery.canRetry}
          isLoading={isCreating}
          onClose={() => {
            handleOpenChange(false);
          }}
          onRetry={resumeRecovery.retry}
          open={open}
        />
      );
    }
    if (isCreating && !showPendingUserForm) {
      return <UserAndCandidacyCreationLoadingIndicator />;
    }

    if (!showPendingUserForm) {
      return (
        <CommandDialog
          // TODO: Better way to position the search bar and dialog separately.
          dialogContentClassName="top-[4%] max-h-[92vh] !translate-y-0 overflow-y-scroll"
          onOpenChange={handleOpenChange}
          open={open}
          shouldFilter={false} // IMPORTANT: Do not change this.
        >
          <CommandInput
            placeholder="Add a candidate by name or phone number"
            onPaste={handlePaste}
            onValueChange={setQuery}
            value={query}
          />
          <CommandList className="max-h-[100vh] overflow-y-scroll">
            {query && (
              <>
                {searchResults?.length === 0 && (
                  // TODO: Small visual quirk where the query updates while waiting for new results after empty results.
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setShowPendingUserForm(true);
                      }}
                    >
                      <PlusIcon className="h-4 w-4" />
                      Create pending user for &quot;{query}&quot;
                    </CommandItem>
                  </CommandGroup>
                )}
                {!!searchResults?.length && !selectedUser && (
                  <CommandGroup>
                    {searchResults.map((user) => (
                      <CommandItem
                        key={user.id}
                        onSelect={() => {
                          handleSelectExistingOrPendingUser({ user });
                        }}
                      >
                        <PlusIcon className="h-4 w-4" />
                        {formatUserName(user)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </CommandDialog>
      );
    }

    return (
      <Dialog
        onOpenChange={(next) => {
          if (isCreating) return;
          if (!next) handleManualExit(ManualExitAction.CLOSE);
        }}
        open={open}
      >
        <DialogContent
          className="top-[4%] max-h-[92vh] !translate-y-0 overflow-y-scroll p-0 sm:max-w-2xl"
          onPointerDownOutside={(event) => {
            event.preventDefault();
          }}
        >
          <PendingUserForm
            defaultStatus={defaultStatus}
            isSubmitting={isCreating}
            initialValues={
              isPhoneNumber(query) ? { phone: query } : { name: query }
            }
            onCancel={() => {
              handleManualExit(ManualExitAction.CANCEL);
            }}
            onFilledChange={setIsPendingUserFormFilled}
            onSubmit={handleCreateUserAndCandidacy}
            submissionError={inlineSubmissionError}
          />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      {renderSearchOrManualDialog()}
      <ConfirmationDialog
        cancelButtonText="Keep editing"
        confirmationButtonVariant="destructive"
        confirmButtonText="Discard"
        description="You have entered candidate information. Closing now will lose it."
        isLoading={isCreating}
        onConfirm={() => {
          if (pendingManualExit) completeManualExit(pendingManualExit);
        }}
        onOpenChange={(next) => {
          if (!next) setPendingManualExit(null);
        }}
        open={pendingManualExit !== null}
        title="Discard candidate?"
      />
      {selectedUser ? (
        isCreating ? (
          <UserAndCandidacyCreationLoadingIndicator />
        ) : (
          <CandidacyDetailsDialog
            defaultStatus={defaultStatus}
            onConfirm={handleSelectExistingOrPendingUserConfirm}
            onOpenChange={(open) => {
              if (!open) setSelectedUser(null);
            }}
            // Rendered only for a selected user, so mounted means open.
            open
            user={selectedUser}
          />
        )
      ) : null}
      {duplicateUser && preservedFormData && (
        <DuplicateUserDialog
          existingUser={duplicateUser}
          isLoading={isCreatingCandidacy}
          onAddToPipeline={handleAddUserToPipeline}
          onOpenChange={(open) => {
            setShowDuplicateUserDialog(open);
            if (!open) {
              setDuplicateUser(null);
              setPreservedFormData(null);
              resetCreateUser();
            }
          }}
          open={showDuplicateUserDialog}
          submittedFormData={preservedFormData}
        />
      )}
    </>
  );
};
